import { supabase } from '../supabase/client';
import { CampaignCreationState, MessageTemplate, LeadList, PresetFilter } from '../types/CampaignCreation';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

interface CampaignCreationResponse {
  success: boolean;
  data?: {
    campaign_id: string;
    sequence_id: string;
    lead_list_ids: string[];
    template_id?: string;
  };
  error?: string;
}

interface CampaignUpdateResponse {
  success: boolean;
  data?: {
    campaign_id: string;
    sequence_id: string;
    lead_list_ids: string[];
    template_id?: string;
  };
  error?: string;
}

const API_BASE_URL = import.meta.env.VITE_SUPABASE_URL + '/functions/v1';

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('You must be logged in to perform this action');
  }
  
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
  };
}

export async function createCampaign(campaignData: CampaignCreationState, csrContext?: { targetUserId: string; csrNotes?: string }): Promise<CampaignCreationResponse> {
  try {
    // Check if this is a CSR creating on behalf of a user
    if (csrContext?.targetUserId) {
      console.log('CSR Context detected, using RPC function for campaign creation');
      
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be logged in to perform this action');
      }

      // Prepare campaign data for RPC function
      const campaignDataJson = {
        campaign_name: campaignData.campaign_name,
        service_tier: campaignData.service_tier,
        lead_acquisition_method: campaignData.lead_acquisition_method,
        selected_preset_filters: campaignData.selected_preset_filters,
        csv_file_data: campaignData.csv_preview_data,
        selected_existing_lists: campaignData.selected_existing_lists,
        sequence_name: campaignData.sequence_name,
        messages: campaignData.messages,
        use_existing_template: campaignData.use_existing_template,
        save_as_template: campaignData.save_as_template,
        template_metadata: campaignData.template_metadata,
        total_estimated_leads: campaignData.total_estimated_leads,
        ai_chatbot_enabled: campaignData.ai_chatbot_enabled,
        status: campaignData.status || 'draft',
        campaign_goal: campaignData.campaignGoal
      };

      // Call RPC function for CSR campaign creation
      const { data, error } = await supabase.rpc('csr_create_campaign_for_user', {
        p_user_id: csrContext.targetUserId,
        p_name: campaignData.campaign_name,
        p_data: campaignDataJson,
        p_csr_notes: csrContext.csrNotes || `CSR created campaign: ${campaignData.campaign_name}`
      });

      if (error) {
        console.error('RPC error:', error);
        throw new Error(error.message || 'Failed to create campaign via CSR');
      }

      // Return in expected format
      return {
        success: true,
        data: {
          campaign_id: data,
          sequence_id: '', // RPC doesn't return this
          lead_list_ids: [],
          template_id: undefined
        }
      };
    }

    // Regular user campaign creation
    const headers = await getAuthHeaders();
    
    // Transform CampaignCreationState to API format
    const apiPayload = {
      campaign_name: campaignData.campaign_name,
      service_tier: campaignData.service_tier,
      lead_acquisition_method: campaignData.lead_acquisition_method,
      selected_preset_filters: campaignData.selected_preset_filters,
      csv_file_data: campaignData.csv_preview_data, // Use preview data for now
      selected_existing_lists: campaignData.selected_existing_lists,
      sequence_name: campaignData.sequence_name,
      messages: campaignData.messages,
      use_existing_template: campaignData.use_existing_template,
      save_as_template: campaignData.save_as_template,
      template_metadata: campaignData.template_metadata,
      total_estimated_leads: campaignData.total_estimated_leads,
      ai_chatbot_enabled: campaignData.ai_chatbot_enabled,
      campaign_goal: campaignData.campaignGoal
    };

    const response = await fetch(`${API_BASE_URL}/createCampaign`, {
      method: 'POST',
      headers,
      body: JSON.stringify(apiPayload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating campaign:', error);
    
    // Provide user-friendly error messages
    let errorMessage = 'Unknown error occurred';
    if (error instanceof Error) {
      if (error.message.includes('logged in')) {
        errorMessage = 'Please log in to create campaigns';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again';
      } else {
        errorMessage = error.message;
      }
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
}

export async function updateCampaign(campaignId: string, campaignData: CampaignCreationState): Promise<CampaignUpdateResponse> {
  try {
    const headers = await getAuthHeaders();
    
    // Transform CampaignCreationState to API format for update
    const apiPayload = {
      campaign_id: campaignId, // Required field for update
      campaign_name: campaignData.campaign_name,
      service_tier: campaignData.service_tier,
      lead_acquisition_method: campaignData.lead_acquisition_method,
      selected_preset_filters: campaignData.selected_preset_filters,
      csv_file_data: campaignData.csv_preview_data, // Use preview data for now
      selected_existing_lists: campaignData.selected_existing_lists,
      lead_source_id: campaignData.lead_source_id, // Add for Previous Target List
      lead_source_type: campaignData.lead_source_type, // Add for Previous Target List
      sequence_name: campaignData.sequence_name,
      messages: campaignData.messages,
      use_existing_template: campaignData.use_existing_template,
      save_as_template: campaignData.save_as_template,
      template_metadata: campaignData.template_metadata,
      total_estimated_leads: campaignData.total_estimated_leads,
      ai_chatbot_enabled: campaignData.ai_chatbot_enabled,
      status: campaignData.status,
      submitted_at: campaignData.submitted_at,
      campaign_goal: campaignData.campaignGoal
    };

    const response = await fetch(`${API_BASE_URL}/updateCampaign`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(apiPayload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating campaign:', error);
    
    // Provide user-friendly error messages
    let errorMessage = 'Unknown error occurred';
    if (error instanceof Error) {
      if (error.message.includes('logged in')) {
        errorMessage = 'Please log in to update campaigns';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again';
      } else if (error.message.includes('Cannot edit campaign')) {
        errorMessage = error.message; // Pass through campaign status validation errors
      } else {
        errorMessage = error.message;
      }
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
}

export async function getUserLeadLists(options: {
  includeInactive?: boolean;
  limit?: number;
  offset?: number;
} = {}): Promise<ApiResponse<LeadList[]>> {
  try {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    
    if (options.includeInactive) params.append('include_inactive', 'true');
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await fetch(`${API_BASE_URL}/getUserLeadLists?${params}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching user lead lists:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export async function getPresetFilters(options: {
  includeInactive?: boolean;
  limit?: number;
  offset?: number;
} = {}): Promise<ApiResponse<PresetFilter[]>> {
  try {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    
    if (options.includeInactive) params.append('include_inactive', 'true');
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await fetch(`${API_BASE_URL}/getPresetFilters?${params}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching preset filters:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export async function getMessageTemplates(options: {
  serviceTier?: 'junior_sdr' | 'top_qualifier';
  category?: string;
  includePublic?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
  all?: boolean;
} = {}): Promise<ApiResponse<MessageTemplate[]>> {
  try {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();

    if (options.serviceTier) params.append('service_tier', options.serviceTier);
    if (options.category) params.append('category', options.category);
    if (options.includePublic) params.append('include_public', 'true');
    if (options.search) params.append('search', options.search);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.all) params.append('all', 'true');

    const response = await fetch(`${API_BASE_URL}/messageTemplates?${params}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching message templates:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export async function createMessageTemplate(templateData: Omit<MessageTemplate, 'id' | 'created_at' | 'updated_at' | 'usage_count'>): Promise<ApiResponse<MessageTemplate>> {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/messageTemplates`, {
      method: 'POST',
      headers,
      body: JSON.stringify(templateData)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating message template:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export async function updateMessageTemplate(templateId: string, updateData: Partial<MessageTemplate>): Promise<ApiResponse<MessageTemplate>> {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/messageTemplates?id=${templateId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating message template:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export async function deleteMessageTemplate(templateId: string): Promise<ApiResponse<{ id: string; deleted: boolean }>> {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/messageTemplates?id=${templateId}`, {
      method: 'DELETE',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error deleting message template:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}